import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IgniteResponse, ToolsInUse } from '@clubhub/ignite/models';
import { AffordabilityLenders, AffordabilityPollResponse, IUiAffordabilityModel, ResultsView } from '@clubhub/ignite/models/affordability';
import { IUiProductsSearchModel } from '@clubhub/ignite/models/products';
import { IUiPropertySearchModel } from '@clubhub/ignite/models/property';
import { AffordabilityOffReason } from '@shared/models';
import { SearchType } from '@shared/models/affordability-enums';
import { CriteriaResponse } from '@shared/models/outcome';
import maxBy from 'lodash-es/maxBy';

const notOnboardedReasonString = 'This lender\'s affordability isn\'t available on Ignite yet';

@Injectable({
  providedIn: 'root',
})
export class IgniteCommonDataService {
  private readonly router = inject(Router);

  private readonly affordabilityOffReasonStrings = new Map<AffordabilityOffReason, string>([
    [AffordabilityOffReason.NotOnboarded, notOnboardedReasonString],
    [AffordabilityOffReason.SwitchedOffForRemapping, 'Temporarily switched off while we update the calculator'],
    [AffordabilityOffReason.SwitchedOffLenderIssues, 'Lender website is experiencing issues, so we have temporarily switched off'],
    [AffordabilityOffReason.ComingSoonOnlyWhenInBuild, 'Coming soon — this lender\'s calculator is on the way!'],
    [AffordabilityOffReason.NoLongerLending, 'Currently not lending'],
  ]);

  public propertyModel: IUiPropertySearchModel | null = null;
  public productModel: IUiProductsSearchModel | null = null;
  public affordabilityModel: IUiAffordabilityModel | null = null;
  public isAffordabilityModelFromProductModel = false;
  public isInaccuracyReport = false;
  public isOutcomeHistory = false;
  public outcomehistoryId: string | null = null;
  public lenders: AffordabilityLenders[] = [];
  public searchToolsInUse: ToolsInUse = {
    affordability: false,
    products: false,
    property: false,
    criteria: false,
  };
  public affordabilityLendersList: string[] = [];
  public resultsView: ResultsView = ResultsView.Details;

  public readonly results$ = signal<AffordabilityPollResponse | CriteriaResponse | null>(null);
  private readonly _source = signal<SearchType | null>(null);

  public get propertyOnly(): boolean {
    return this.router.url.includes('property') && !this.router.url.includes('affordability');
  }

  public get criteriaOnly(): boolean {
    return this.router.url.includes('criteria') && !this.router.url.includes('affordability');
  }

  public get affordabilityOnly(): boolean {
    return this.router.url.includes('affordability');
  }

  public get restoredCriteriaOnly(): boolean {
    return this.router.url.includes('restoreCriteria');
  }

  public get productsOnly(): boolean {
    return this.router.url.includes('products');
  }

  public get results() {
    return this.results$();
  }

  public set results(value: AffordabilityPollResponse | CriteriaResponse | null) {
    this.results$.set(value);
  }

  /**
   * Re-publishes the current results with fresh object references.
   *
   * Some values on a quote are not returned by the affordability API and are derived from the product results
   * instead - for BTL, maximumAffordableLoanAmount / maximumValue / stressRate are worked out from the matched
   * products and written onto the quote object *in place* (see ProductsSearchService.updateLenderAffordabilityMet).
   * The lender result rows are OnPush and bind the quote object itself, so an in-place write is invisible to
   * them; they only re-render when the object reference changes. During a live search that happens by luck,
   * because affordability keeps polling after the products have loaded and every poll replaces this signal.
   * When products load *after* the final poll - a saved search rerun, for example - nothing replaces it, and
   * those lenders render "-" even though the value is sitting on the quote. Call this after enriching quotes.
   */
  public refreshResults(): void {
    const current = this.results$();
    if (!current?.quotes?.length) {
      return;
    }

    this.results$.set({ ...current, quotes: current.quotes.map(quote => ({ ...quote })) });
  }

  public get source() {
    return this._source();
  }

  public set source(source: SearchType | null) {
    this._source.set(source);
  }

  public readonly allAffordabilityLenders = computed(() =>
    this.results$()?.quotes?.map(x => x.lenderName).toSorted((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })) ?? [],
  );

  public get maxMalaResult(): IgniteResponse | undefined {
    return maxBy(this.results as any, (o: IgniteResponse) => o.maximumAffordableLoanAmount);
  }

  public get affordabilitySearchId(): string | null {
    return (this.results as AffordabilityPollResponse)?.affordabilitySearchId ?? null;
  }

  public get pollJobId() {
    return this.results?.pollJobID;
  }

  public get hasAffordability(): boolean {
    return !!this.results?.quotes?.length;
  }

  public reset() {
    this.propertyModel = null;
    this.productModel = null;
    this.affordabilityModel = null;
    this.isAffordabilityModelFromProductModel = false;
    this.source = null;
    this.isInaccuracyReport = false;
    this.isOutcomeHistory = false;
    this.searchToolsInUse = {
      affordability: false,
      products: false,
      property: false,
      criteria: false,
    };
  }

  public get isHistoricResult() {
    return this.isInaccuracyReport || this.isOutcomeHistory;
  }

  public isNetFreePayTooLow(result: IgniteResponse): boolean {
    return result.error?.statusCode === 'NetFreePayTooLow' || (result.error?.message?.includes('NetFreePayTooLow') ?? false);
  }

  public isAffordabilityMet(affordabilityResult: IgniteResponse) {
    return (
      (affordabilityResult.maximumValue ?? -1) >= (this.affordabilityModel?.propertyAndLoan?.loanAmount ?? 0) &&
      !this.isNetFreePayTooLow(affordabilityResult)
    );
  }

  public get isDetailsView(): boolean {
    return this.resultsView === ResultsView.Details;
  }

  public get isSummaryView(): boolean {
    return this.resultsView === ResultsView.Summary;
  }

  public setResultsView(view: ResultsView) {
    this.resultsView = view;
  }

  public get isMatchedProductsView(): boolean {
    return this.resultsView === ResultsView.MatchedProducts;
  }

  public get isViewAllProductsView(): boolean {
    return this.resultsView === ResultsView.ViewAllProducts;
  }

  public get isComparedProductsView(): boolean {
    return this.resultsView === ResultsView.ComparedProducts;
  }

  public get isInputView(): boolean {
    return this.resultsView === ResultsView.Input;
  }

  public get isProductView(): boolean {
    return this.isMatchedProductsView || this.isViewAllProductsView || this.isComparedProductsView;
  }

  public getNoAffordabilityMessage(result: IgniteResponse | undefined): string {
    return (
      this.affordabilityOffReasonStrings.get(result?.affordabilityOffReason ?? AffordabilityOffReason.NotOnboarded) ??
      notOnboardedReasonString
    );
  }
}
